import { t } from 'i18next'
import { OrganisationWithUserAccountsDTO } from '../api/schemas/schema'
import {
   Components,
   DemandComplianceModuleDefinition,
   EmailRequestDTO,
   OrganisationSubscriptionDetailsDTO,
   Process,
   Recipient,
   RiskManagementModules,
   SubscriptionLevel,
} from '../api/schemas/schema'

export const createUserUpdateEmailRequest = (
   form: OrganisationWithUserAccountsDTO,
   previousData: OrganisationWithUserAccountsDTO,
   demandComplianceModuleDefinitions: DemandComplianceModuleDefinition[],
   processes: Process[]
) => {
   const formatSubscriptions = (subs: SubscriptionLevel[] | undefined) =>
      subs
         ? subs
              .sort((a, b) => a - b)
              .map((level) => t(SubscriptionLevel[level]))
              .join(', ')
         : ''

   const subscriptionDetails: OrganisationSubscriptionDetailsDTO = {
      userCount: form.userAccounts.length ?? null,
      subscriptions: formatSubscriptions(form.organisation.subscriptions),
      baseModules: getBaseModules(form),
      demandModules: getDemandModules(form, demandComplianceModuleDefinitions),
      riskModules: getRiskModules(form),
      processes: getOrganisationProcesses(form, processes),
   }

   const hasChanges =
      subscriptionDetails.userCount !== previousData?.userAccounts?.length ||
      subscriptionsHasChanged(form, previousData) ||
      modulesHasChanged(form, previousData) ||
      processesHasChanged(form, previousData)

   if (hasChanges) {
      const adminUser = form.userAccounts[0]
      const recipient: Recipient = {
         email: adminUser.email,
         name: `${adminUser.firstName} ${adminUser.lastName}`,
      }
      const request: EmailRequestDTO = {
         recipients: [recipient],
         organisationName: form.organisation.name,
         subscriptionDetails: subscriptionDetails,
      }
      return request
   }

   return null
}

const getBaseModules = (form: OrganisationWithUserAccountsDTO): string[] =>
   (form.organisation.modules ?? [])
      .filter((module) => module.component !== undefined && module.component !== null)
      .map((module) => t(Components[module.component]))

const getDemandModules = (form: OrganisationWithUserAccountsDTO, definitions: DemandComplianceModuleDefinition[]): string[] =>
   (form.organisation.modules ?? [])
      .filter((module) => module.demandComplianceModuleDefinitionId !== undefined && module.demandComplianceModuleDefinitionId !== null)
      .map((module) => {
         const definition = definitions.find((d) => d.id === module.demandComplianceModuleDefinitionId)
         return definition?.name ?? module.demandComplianceModuleDefinitionId
      })

const getRiskModules = (form: OrganisationWithUserAccountsDTO): string[] =>
   (form.organisation.modules ?? [])
      .filter((module) => module.riskManagementModule !== undefined && module.riskManagementModule !== null)
      .map((module) => t(RiskManagementModules[module.riskManagementModule]))

const getOrganisationProcesses = (form: OrganisationWithUserAccountsDTO, processes: Process[]): string[] =>
   (form.organisation.organisationProcessIds ?? []).map((id) => {
      const process = processes.find((p) => p.id === id)
      return process?.text ?? id
   })

const subscriptionsHasChanged = (form: OrganisationWithUserAccountsDTO, previousData: OrganisationWithUserAccountsDTO): boolean => {
   const current = form.organisation.subscriptions ?? []
   const previous = previousData?.organisation?.subscriptions ?? []
   return current.length !== previous.length || current.some((s) => !previous.includes(s))
}

const processesHasChanged = (form: OrganisationWithUserAccountsDTO, previousData: OrganisationWithUserAccountsDTO): boolean => {
   const current = form.organisation.organisationProcessIds ?? []
   const previous = previousData?.organisation?.organisationProcessIds ?? []
   return current.length !== previous.length || current.some((id) => !previous.includes(id))
}

const modulesHasChanged = (form: OrganisationWithUserAccountsDTO, previousData: OrganisationWithUserAccountsDTO): boolean => {
   return form.organisation.modules.some((module) => {
      const prevModule = previousData.organisation.modules.find((prevModule) => prevModule.id === module.id)

      return (
         !prevModule ||
         module.isActivated !== prevModule.isActivated ||
         module.demandComplianceModuleDefinitionId !== prevModule.demandComplianceModuleDefinitionId ||
         module.generalModule !== prevModule.generalModule ||
         module.riskManagementModule !== prevModule.riskManagementModule
      )
   })
}
